import { UtilisateurBase, type ICiam } from '@/features/auth/authentification'
import { InteractionRequiredAuthError, InteractionType, type AuthenticationResult } from '@azure/msal-browser'
import { aktoTenantId, interactionType, loginRequest, logoutUri, scopesExtranetApi } from '../index'

import { defineStore } from 'pinia'
import { useMsalStore } from './useMsalStore'

export const useCiamStore = defineStore('ciam', () => {
  const azureAd = useMsalStore()

  async function getUtilisateurCourantAsync(): Promise<UtilisateurBase | null> {
    const account = azureAd.msalInstance.getActiveAccount()
    if (!account) return null

    const estInterne = account.homeAccountId.endsWith(aktoTenantId)

    const claims = (await getAuthTokenAsync(scopesExtranetApi)).idTokenClaims as any

    const nom = claims.family_name || null
    const prenom = claims.given_name || null

    return new UtilisateurBase(account.localAccountId, account.username, nom, prenom, null, estInterne)
  }

  function getIdUtilisateurCourant(): string | null {
    return azureAd.accounts?.at(0)?.localAccountId ?? null
  }

  async function attendreFlotAuthentificationAsync() {
    if (interactionType === InteractionType.Redirect) {
      await azureAd.msalInstance.handleRedirectPromise()
    }
  }

  async function authentificationRequiseAsync(to: string, inscription: boolean): Promise<boolean> {
    const request = {
      ...loginRequest(inscription),
      redirectStartPage: to,
    }
    try {
      await attendreFlotAuthentificationAsync()

      if (azureAd.accounts.length > 0) return true

      if (interactionType === InteractionType.Popup) {
        await azureAd.msalInstance.loginPopup(request)
      } else if (interactionType === InteractionType.Redirect) {
        await azureAd.msalInstance.loginRedirect(request)
      }
      return true
    } catch {
      return false
    }
  }

  async function getAuthTokenAsync(scopes: Array<string> = scopesExtranetApi): Promise<AuthenticationResult> {
    const account = azureAd.msalInstance.getActiveAccount()
    if (!account) throw 'Utilisateur déconnecté'

    const accessTokenRequest = {
      scopes,
      account,
    }

    try {
      return await azureAd.msalInstance.acquireTokenSilent(accessTokenRequest)
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        if (interactionType === InteractionType.Popup) {
          return await azureAd.msalInstance.acquireTokenPopup(accessTokenRequest)
        } else {
          await azureAd.msalInstance.acquireTokenRedirect(accessTokenRequest)
          throw 'Jamais atteint a cause de la redirection'
        }
      } else {
        throw error
      }
    }
  }

  function getBearerTokenAsync(scopes: Array<string> = scopesExtranetApi): Promise<string> {
    return getAuthTokenAsync(scopes).then((token) => token.accessToken)
  }

  async function deconnexionAsync() {
    const account = azureAd.accounts.at(0)
    if (!account) return
    const logoutRequest = {
      account,
      mainWindowRedirectUri: logoutUri,
    }

    try {
      if (interactionType === InteractionType.Redirect) {
        await azureAd.msalInstance.logoutRedirect(logoutRequest)
      } else {
        await azureAd.msalInstance.logoutPopup(logoutRequest)
      }
    } catch (error) {
      console.error(error)
    }
  }

  return {
    attendreFlotAuthentificationAsync,
    authentificationRequiseAsync,
    deconnexionAsync,
    getUtilisateurCourantAsync,
    getIdUtilisateurCourant,
    getBearerTokenAsync,
  } as ICiam
})
